import { memo, useMemo, useEffect, useCallback } from 'react'
import type { FC } from 'react'
import {
	Heading,
	Box,
	Text,
	Flex,
	LinkOverlay,
	CloseButton,
	Link as ChakraLink,
	Grid,
} from '@chakra-ui/react'
import { Link } from 'react-router-dom'
import type { LocationDescriptor } from 'history'
import { displayAddress, formatInt } from '@alexanderathoodly/data-models'
import type { CoworkingLocation } from '@alexanderathoodly/data-models'
import { SIZE_290, SIZE_410 } from '@yta/image-proxy'
import {
	ListResult,
	ListResultHeader,
	ListResultIllustration,
	ListResultActions,
	Carousel,
	CarouselBackArrow,
	CarouselForwardArrow,
	CarouselSlide,
	PinIcn,
	Tag,
	convertChakraColorToCSS,
	mediaQuerybreakpoints as mq,
} from '@alexanderathoodly/ui-library'
import { useCoworkingSerpParams, useSerpPageTypeInSwedish } from './utils'
import { GAAction, sendGA4Event } from '~/analytics/ga'
import { ImgProxy } from '~/utils/imgproxy'
import { css } from '@emotion/react'
import {
	FILTER_TYPE_MEMBERSHIP,
	FILTER_TYPE_OFFICE,
	COWORKING_TYPE,
	SERVICED_OFFICE_TYPE,
	OFFICE_TYPE,
	FILTER_TYPE_BIG_OFFICE,
} from './urlGenerator'
import { useTranslation, Trans } from 'react-i18next'
import { dateToString } from '~/utils/text'

const useLocationTo: (
	location: CoworkingLocation
) => LocationDescriptor<unknown> = (location) => {
	const { t } = useTranslation()
	const searchParams = useCoworkingSerpParams()
	const targetParams = new URLSearchParams()
	if (searchParams.min_workspaces) {
		targetParams.append('min_ws', searchParams.min_workspaces.toString())
	}
	if (searchParams.max_workspaces) {
		targetParams.append('max_ws', searchParams.max_workspaces.toString())
	}
	if (searchParams.budget) {
		targetParams.append('budget', searchParams.budget.toString())
	}
	if (searchParams.types) {
		for (const type of searchParams.types) {
			targetParams.append('type', type)
		}
	}
	const pathBase = location.is_flex_office
		? t('/kontor', { ns: 'url' })
		: t('/kontorshotell', { ns: 'url' })
	return {
		pathname: `${pathBase}/${location.url}`,
		search: targetParams.toString(),
	}
}

const coworkingSolutionSerpCardCss = {
	heading: css({
		fontSize: '1.5rem !important',
		fontWeight: '600',
		color: convertChakraColorToCSS('grey.800'),
		display: '-webkit-box',
		WebkitLineClamp: 1,
		textOverflow: 'ellipsis',
		overflow: 'hidden',
		WebkitBoxOrient: 'vertical',
	}),
	addressInfo: {
		self: css({ alignItems: 'center' }),
		pin: {
			outer: {
				fill: 'blue.500',
				stroke: 'blue.500',
			},
			inner: { fill: 'white' },
			pinCss: css({ height: '1rem', width: '1rem', marginRight: '0.25rem' }),
		},
		address: css({
			color: convertChakraColorToCSS('grey.500'),
			marginTop: '0.5rem',
			marginBottom: '0.25rem',
		}),
	},
	highlights: css({
		flexDirection: 'row',
		columnGap: '0.8rem',
		flexWrap: 'wrap',
	}),
	summary: {
		self: css({
			marginBottom: '1.5rem',
			gridTemplateRows: 'repeat(2, auto)',
			color: convertChakraColorToCSS('grey.500'),
		}),
		solutionInfo: css({
			gridRow: '2/3',
			flexDirection: 'column',
			justifyContent: 'space-between',
			marginTop: '0.75rem',
			marginBottom: 0,
			gap: '0.5rem',
			[mq[1] as string]: {
				gridRow: '1/2',
				marginTop: 0,
				marginBottom: '0.5rem',
			},
		}),
		description: css({
			gridRow: '1/2',
			[mq[1] as string]: {
				gridRow: '2/3',
				display: '-webkit-box',
				WebkitLineClamp: 3,
				textOverflow: 'ellipsis',
				overflow: 'hidden',
				WebkitBoxOrient: 'vertical',
			},
		}),
	},
}

function useMembershipPrice(result: CoworkingLocation): {
	price: number
	priceUnit: string
} | null {
	const { t } = useTranslation()
	const serpType = useSerpPageTypeInSwedish()
	const { min_workspaces, types } = useCoworkingSerpParams()
	const { min_rent_membership } = result

	const priceUnit = min_workspaces ? t('kr/mån') : t('kr/person/mån')
	const membershipPrice = useMemo(() => {
		if (
			serpType != COWORKING_TYPE ||
			(types.length && !types.includes(FILTER_TYPE_MEMBERSHIP)) ||
			!min_rent_membership?.price
		) {
			return null
		}
		const price = min_workspaces
			? min_rent_membership && min_rent_membership?.price * min_workspaces
			: min_rent_membership?.price
		return { price, priceUnit }
	}, [min_rent_membership, types, priceUnit, serpType, min_workspaces])

	return membershipPrice
}

function useOfficePrice(result: CoworkingLocation): {
	translationKey: string
	price: string
	priceUnit: string
	extraDetails: string | null
} | null {
	const { t } = useTranslation()
	const serpType = useSerpPageTypeInSwedish()
	const { min_workspaces: filter_min_workspaces, types } =
		useCoworkingSerpParams()
	const { min_rent_solution, min_workspace_rent, rooms_solution_info } = result
	const min_size = rooms_solution_info?.min_size
	const min_workspaces = rooms_solution_info?.min_workspaces
	const max_size = rooms_solution_info?.max_size
	const max_workspaces = rooms_solution_info?.max_workspaces

	const price = useMemo(() => {
		const price = min_rent_solution
			? filter_min_workspaces
				? min_rent_solution.price
				: min_workspace_rent || 0
			: 0
		return formatInt(Math.round(price / 100) * 100)
	}, [min_rent_solution, min_workspace_rent, filter_min_workspaces])
	const priceUnit = filter_min_workspaces ? t('kr/mån') : t('kr/person/mån')

	const translationKey = useMemo(() => {
		if (
			serpType == COWORKING_TYPE ||
			(types.length == 2 &&
				types.includes(FILTER_TYPE_MEMBERSHIP) &&
				types.includes(FILTER_TYPE_OFFICE))
		) {
			return 'Kontorsrum på coworking från <bold>{{price}}</bold> {{priceUnit}}'
		}
		if (
			serpType == SERVICED_OFFICE_TYPE ||
			(types.length == 2 &&
				types.includes(FILTER_TYPE_OFFICE) &&
				types.includes(FILTER_TYPE_OFFICE))
		) {
			return 'Kontorsrum från <bold>{{price}}</bold> {{priceUnit}}'
		}
		return 'Kontor från <bold>{{price}}</bold> {{priceUnit}}'
	}, [serpType, types])

	const extraDetails = useMemo(() => {
		if (
			serpType == OFFICE_TYPE ||
			(types.length === 3 && !types.includes(FILTER_TYPE_MEMBERSHIP))
		) {
			if (!min_workspaces && !max_workspaces) {
				return null
			}
			let wsContent = ''
			if (
				(filter_min_workspaces && min_workspaces) ||
				(min_workspaces && min_workspaces === max_workspaces)
			) {
				wsContent = formatInt(min_workspaces)
			}
			if (
				!filter_min_workspaces &&
				min_workspaces &&
				max_workspaces &&
				min_workspaces !== max_workspaces
			) {
				wsContent = `${formatInt(min_workspaces)} - ${formatInt(
					max_workspaces
				)}`
			}
			if (!wsContent) {
				return null
			}
			return 'Storlek från <bold>' + wsContent + '</bold> arbetsplatser'
		}
		if (!min_size || !max_size) {
			return null
		}
		let sizeContent = ''
		if (
			(filter_min_workspaces && min_size) ||
			(min_size && min_size === max_size)
		) {
			sizeContent = formatInt(min_size)
		}
		if (
			!filter_min_workspaces &&
			min_size &&
			max_workspaces &&
			min_size !== max_size
		) {
			sizeContent = `${formatInt(min_size)} - ${formatInt(max_size)}`
		}
		if (!sizeContent) {
			return null
		}
		return 'Storlek från <bold>' + sizeContent + ' </bold> m²'
	}, [
		filter_min_workspaces,
		serpType,
		max_workspaces,
		min_workspaces,
		min_size,
		max_size,
		types,
	])

	return { price, priceUnit, translationKey, extraDetails }
}

function useBigOfficePrice(result: CoworkingLocation): {
	translationKey: string | null
	price: string
	priceUnit: string
	extraDetails: string | null
} | null {
	const { t } = useTranslation()
	const serpType = useSerpPageTypeInSwedish()
	const { min_workspaces: filter_min_workspaces, types } =
		useCoworkingSerpParams()
	const { big_office_info } = result
	const min_price = big_office_info?.min_price
	const min_size = big_office_info?.min_size
	const min_workspaces = big_office_info?.min_workspaces
	const min_ws_price = big_office_info?.min_ws_price
	const max_size = big_office_info?.max_size
	const max_workspaces = big_office_info?.max_workspaces

	const price = useMemo(() => {
		const price = min_price
			? filter_min_workspaces
				? min_price
				: min_ws_price || 0
			: 0
		return formatInt(Math.round(price / 100) * 100)
	}, [filter_min_workspaces, min_price, min_ws_price])

	const priceUnit = filter_min_workspaces ? t('kr/mån') : t('kr/person/mån')

	const translationKey = useMemo(() => {
		if (
			serpType == COWORKING_TYPE ||
			(types.length == 2 &&
				types.includes(FILTER_TYPE_MEMBERSHIP) &&
				types.includes(FILTER_TYPE_OFFICE))
		) {
			return null
		}

		return 'Egen avdelning från <bold>{{price}}</bold> {{priceUnit}}'
	}, [serpType, types])

	const extraDetails = useMemo(() => {
		if (
			serpType == COWORKING_TYPE ||
			(types.length == 2 &&
				types.includes(FILTER_TYPE_MEMBERSHIP) &&
				types.includes(FILTER_TYPE_OFFICE))
		) {
			return null
		}
		if (
			serpType == SERVICED_OFFICE_TYPE ||
			(types.length == 2 &&
				types.includes(FILTER_TYPE_OFFICE) &&
				types.includes(FILTER_TYPE_BIG_OFFICE))
		) {
			if (!min_size || !max_size) {
				return null
			}
			let sizeContent = ''
			if (
				(filter_min_workspaces && min_size) ||
				(min_size && min_size === max_size)
			) {
				sizeContent = formatInt(min_size)
			}
			if (min_size && max_workspaces && min_size !== max_size) {
				sizeContent = `${formatInt(min_size)} - ${formatInt(max_size)}`
			}
			if (!sizeContent) {
				return null
			}
			return 'Storlek från <bold>' + sizeContent + ' </bold> m²'
		}
		if (!min_workspaces && !max_workspaces) {
			return null
		}
		let wsContent = ''
		if (
			(filter_min_workspaces && min_workspaces) ||
			(min_workspaces && min_workspaces === max_workspaces)
		) {
			wsContent = formatInt(min_workspaces)
		}
		if (min_workspaces && max_workspaces && min_workspaces !== max_workspaces) {
			wsContent = `${formatInt(min_workspaces)} - ${formatInt(max_workspaces)}`
		}
		if (!wsContent) {
			return null
		}
		return 'Storlek från <bold>' + wsContent + '</bold> arbetsplatser'
	}, [
		filter_min_workspaces,
		serpType,
		max_workspaces,
		min_workspaces,
		min_size,
		max_size,
		types,
	])
	if (
		serpType == COWORKING_TYPE ||
		(types.length == 2 &&
			types.includes(FILTER_TYPE_MEMBERSHIP) &&
			types.includes(FILTER_TYPE_OFFICE))
	) {
		return null
	}

	return { price, priceUnit, translationKey, extraDetails }
}

const FlexOfficeSummary: FC<{ result: CoworkingLocation }> = ({ result }) => {
	const { t } = useTranslation()
	const {
		min_workspaces: filter_min_workspaces,
		max_workspaces: filter_max_workspaces,
		budget,
	} = useCoworkingSerpParams()
	const hasFilters =
		!!filter_min_workspaces || !!filter_max_workspaces || !!budget

	return (
		<>
			<Flex justifyContent="space-between">
				{result.available_flex_offices && result.available_flex_offices > 0 ? (
					result.flex_office_info ? (
						<>
							{hasFilters ? (
								<Text>
									<Trans
										i18nKey="Egna kontor från <bold>begär offert</bold> kr/mån"
										components={{
											bold: <b />,
										}}
									/>
								</Text>
							) : result.flex_office_info.min_workspaces ? (
								<Text>
									{result.flex_office_info.min_workspaces ==
									result.flex_office_info.max_workspaces ? (
										<Trans
											i18nKey="Egna kontor från <bold>{{min_flex_office_ws}}</bold> arbetsplatser"
											values={{
												min_flex_office_ws:
													result.flex_office_info.min_workspaces,
											}}
											components={{
												bold: <b />,
											}}
										/>
									) : (
										<Trans
											i18nKey="Egna kontor för mellan <bold>{{min_flex_office_ws}} - {{max_flex_office_ws}}</bold> arbetsplatser"
											values={{
												min_flex_office_ws:
													result.flex_office_info.min_workspaces,
												max_flex_office_ws:
													result.flex_office_info.max_workspaces,
											}}
											components={{
												bold: <b />,
											}}
										/>
									)}
								</Text>
							) : null}
							{result.flex_office_info.min_size ? (
								<Text>
									{result.flex_office_info.min_size ==
										result.flex_office_info.max_size || hasFilters ? (
										<Trans
											i18nKey="Egna kontor från <bold>{{size}}kvm</bold>"
											values={{ size: result.flex_office_info.min_size }}
											components={{
												bold: <b />,
											}}
										/>
									) : (
										<Trans
											i18nKey="Egna kontor mellan <bold>{{min_size}} - {{max_size}}kvm</bold>"
											values={{
												min_size: result.flex_office_info.min_size,
												max_size: result.flex_office_info.max_size,
											}}
											components={{
												bold: <b />,
											}}
										/>
									)}
								</Text>
							) : null}
						</>
					) : null
				) : (
					<Text>
						{t(
							'Inga tillgängliga kontor som matchar ditt filter för tillfället'
						)}
					</Text>
				)}
			</Flex>
		</>
	)
}

const SummaryDisplay: FC<{ result: CoworkingLocation }> = ({ result }) => {
	const { i18n } = useTranslation()
	const { summary } = coworkingSolutionSerpCardCss

	const officePrice = useOfficePrice(result)
	const membershipPrice = useMembershipPrice(result)
	const bigOfficePrice = useBigOfficePrice(result)

	return (
		<Grid css={summary.self}>
			<Flex css={summary.solutionInfo} direction="column">
				{result.is_flex_office ? (
					<FlexOfficeSummary result={result} />
				) : (
					<>
						{officePrice && officePrice.price != '0' && (
							<Flex display="flex" justifyContent="space-between">
								<>
									<Text>
										<Trans
											i18nKey={officePrice.translationKey}
											values={{
												price: officePrice.price,
												priceUnit: officePrice.priceUnit,
											}}
											components={{
												bold: <b />,
											}}
										/>
									</Text>
									{officePrice.extraDetails && (
										<Text>
											<Trans
												i18nKey={officePrice.extraDetails}
												components={{
													bold: <b />,
												}}
											/>
										</Text>
									)}
								</>
							</Flex>
						)}
						{bigOfficePrice &&
							bigOfficePrice.price != '0' &&
							bigOfficePrice.translationKey && (
								<Flex display="flex" justifyContent="space-between">
									<>
										<Text>
											<Trans
												i18nKey={bigOfficePrice.translationKey}
												values={{
													price: bigOfficePrice.price,
													priceUnit: bigOfficePrice.priceUnit,
												}}
												components={{
													bold: <b />,
												}}
											/>
										</Text>
										{bigOfficePrice.extraDetails && (
											<Text>
												<Trans
													i18nKey={bigOfficePrice.extraDetails}
													components={{
														bold: <b />,
													}}
												/>
											</Text>
										)}
									</>
								</Flex>
							)}
						{membershipPrice && membershipPrice.price != 0 && (
							<Flex display="flex">
								<Text>
									<Trans
										i18nKey="Medlemskap från <bold>{{price}}</bold> {{priceUnit}}"
										values={{
											price: formatInt(membershipPrice.price),
											priceUnit: membershipPrice.priceUnit,
										}}
										components={{
											bold: <b />,
										}}
									/>
								</Text>
							</Flex>
						)}
					</>
				)}
			</Flex>
			<Text itemProp="description" css={summary.description}>
				{i18n.language != 'en'
					? result.short_description
					: result.short_description_en}
			</Text>
		</Grid>
	)
}

const NewContentTag: FC<{
	result: CoworkingLocation
}> = ({ result }) => {
	const { t } = useTranslation()
	const latestDate = result.is_flex_office
		? result.latest_flex_office_date
		: result.latest_solution_date
	const isNew = latestDate
		? (new Date().getTime() - new Date(latestDate).getTime()) /
				(1000 * 60 * 60 * 24) <=
		  14
		: false

	if (isNew && latestDate) {
		return (
			<Tag key={`new-content-${result.id}`} color="light-blue" size="font12">
				<Text>
					{`${
						result.is_flex_office
							? t('Nya kontor tillagda')
							: t('Nya rum tillagda')
					} ${dateToString(new Date(latestDate))}`}
				</Text>
			</Tag>
		)
	}
	return null
}

export const CoworkingSolutionSerpCard: FC<{
	result: CoworkingLocation
	locationIndex: number
	isFeaturedListingCard?: boolean
}> = ({ result, locationIndex, isFeaturedListingCard = false }) => {
	const { t } = useTranslation()
	const to = useLocationTo(result)
	const sendGAAction = useCallback(
		(action: 'view' | 'click') => {
			GAAction({
				category: 'site_interaction',
				action: action,
				label: 'featuredListing',
				metrics: {
					metric2: result.id,
					metric3: result.provider_name,
				},
			})
			sendGA4Event('featured_listing', {
				type: action,
				id: result.id,
				provider_name: result.provider_name,
			})
		},
		[result.id, result.provider_name]
	)
	useEffect(() => {
		if (isFeaturedListingCard) {
			sendGAAction('view')
		}
	}, [isFeaturedListingCard, sendGAAction])
	const { heading, addressInfo, highlights } = coworkingSolutionSerpCardCss
	const solutionCount = result.is_flex_office
		? result.available_flex_offices
		: result.solutions_count

	return (
		<ListResult
			isPremium={result.is_premium_listing}
			itemScope
			itemType="https://schema.org/LocalBusiness"
		>
			<ListResultHeader>
				<LinkOverlay
					as={Link}
					to={to}
					onClick={() => {
						if (isFeaturedListingCard) {
							sendGAAction('click')
						}
					}}
				>
					<Flex>
						<Heading itemProp="name" css={heading}>
							{result.name}
						</Heading>
						<ImgProxy
							src={result.provider_logo}
							sizeSet={{ base: SIZE_290 }}
							loading={locationIndex > 3 ? 'lazy' : 'eager'}
							alt={`${result.provider_name} - logo`}
							ml="auto"
							maxH="3.6rem"
							maxW="3.6rem"
							display={result.is_premium_listing ? 'inline-flex' : 'none'}
						/>
					</Flex>
				</LinkOverlay>
				<Flex
					itemProp="address"
					itemScope
					itemType="https://schema.org/PostalAddress"
					css={coworkingSolutionSerpCardCss.addressInfo.self}
				>
					<PinIcn
						outer={addressInfo.pin.outer}
						inner={addressInfo.pin.inner}
						css={addressInfo.pin.pinCss}
					/>
					<Text css={addressInfo.address}>
						{displayAddress(result.address).replace(
							',',
							`, ${result.breadcrumbs.area.name}, `
						)}
					</Text>
					<meta itemProp="addressLocality" content={result.address.locator} />
					<meta itemProp="addressRegion" content={result.address.city} />
					<meta itemProp="streetAddress" content={result.address.street} />
				</Flex>
				<Flex css={highlights}>
					{result.highlights.map((highlight, index) => (
						<Tag
							key={`serpcard-${result.id}-${index}`}
							color="light-blue"
							size="font12"
						>
							{t(highlight)}
						</Tag>
					))}
					{solutionCount > 0 && (
						<Box display={{ base: 'none', lg: 'flex' }}>
							<Tag
								key={`serpcard-${result.id}-solution-count`}
								color="light-blue"
								size="font12"
							>
								<Text>
									<b>{solutionCount}</b>{' '}
									{t('ledigt kontor just nu', {
										count: solutionCount,
									})}
								</Text>
							</Tag>
						</Box>
					)}
					<NewContentTag result={result} />
				</Flex>
			</ListResultHeader>
			<ListResultIllustration>
				<Box position="relative" zIndex={-1} maxH="max-content" maxW="25rem">
					<Box
						position="absolute"
						top={3}
						left={3}
						px={3}
						py={1}
						borderRadius="sm"
						backgroundColor="#EBC22A"
						display={isFeaturedListingCard ? 'unset' : 'none'}
					>
						{t('Betald placering')}
					</Box>
					<ImgProxy
						src={result.images[0] || ''}
						sizeSet={{ base: SIZE_410 }}
						loading={locationIndex > 3 ? 'lazy' : 'eager'}
						alt={t('kontorshotell i {{city}} - {{location_name}}', {
							ns: 'meta_data',
							city: result.address.city,
							location_name: result.name,
						})}
						itemProp="image"
					/>
					{solutionCount > 0 && (
						<Box
							position="absolute"
							bottom={3}
							right={3}
							px={3}
							py={1}
							borderRadius="sm"
							backgroundColor="blue.50"
							display={{ lg: 'none' }}
						>
							<Text>
								<b>{solutionCount}</b>{' '}
								{solutionCount > 1 ? t('lediga') : t('ledigt')}{' '}
								{t('kontor just nu')}
							</Text>
						</Box>
					)}
					<Grid
						gridTemplateRows="1fr"
						gridTemplateColumns="1fr 1fr"
						gap=".3rem"
						mt=".3rem"
						display={
							result.is_premium_listing && result.images.length > 1
								? 'grid'
								: 'none'
						}
					>
						<ImgProxy
							src={result.images[1] || ''}
							sizeSet={{ base: SIZE_290 }}
							loading={locationIndex > 3 ? 'lazy' : 'eager'}
							alt={t('kontorshotell i {{city}} - {{location_name}}', {
								ns: 'meta_data',
								city: result.address.city,
								location_name: result.name,
							})}
							itemProp="image"
						/>
						<ImgProxy
							src={result.images[2] || ''}
							sizeSet={{ base: SIZE_290 }}
							loading={locationIndex > 3 ? 'lazy' : 'eager'}
							alt={t('kontorshotell i {{city}} - {{location_name}}', {
								ns: 'meta_data',
								city: result.address.city,
								location_name: result.name,
							})}
							itemProp="image"
						/>
					</Grid>
				</Box>
			</ListResultIllustration>
			<ListResultActions>
				<SummaryDisplay result={result} />
				<Box
					px={3}
					py={1}
					borderRadius="sm"
					backgroundColor="#EBC22A"
					display={result.is_premium_listing ? 'inline-flex' : 'none'}
				>
					{t('Premium')}
				</Box>
			</ListResultActions>
			<meta itemProp="additionalType" content={'Coworking office'} />
			<meta itemProp="url" content={result.url} />
			<meta itemProp="brand" content={result.provider_name} />
			<meta
				itemProp="keywords"
				content={result.highlights.map((highlight) => t(highlight)).toString()}
			/>
			<meta
				itemProp="tourBookingPage"
				content={`https://yta.se/kontorshotell/${result.url}`}
			/>
		</ListResult>
	)
}

export const CoworkingSolutionMapCard: FC<{
	result: CoworkingLocation
	onClickEvent?: () => void
}> = memo(({ result, onClickEvent }) => {
	const { t } = useTranslation()
	const to = useLocationTo(result)
	const carouselSlides = result.images.map((img, index) => (
		<CarouselSlide key={img}>
			<ImgProxy
				src={img}
				sizeSet={{ base: SIZE_290 }}
				h={20}
				w="min-content"
				loading="lazy"
				alt={`${result.name} bild ${index + 1}`}
			/>
		</CarouselSlide>
	))
	const solutionCount = result.is_flex_office
		? result.available_flex_offices
		: result.solutions_count
	return (
		<Box
			position="absolute"
			bottom={5}
			left={{ base: 1, md: 5 }}
			p={{ base: 5, md: 7 }}
			w={{ base: 'xs', md: 'lg', lg: 'xl' }}
			bg="white"
			borderColor="grey.100"
			borderStyle="solid"
			borderWidth="thin"
			borderRadius="base"
			zIndex={5}
		>
			<Flex mb={3} alignItems="center">
				<ChakraLink
					as={Link}
					to={to}
					target="_blank"
					variant="black"
					flex={1}
					overflow="hidden"
				>
					<Heading
						fontSize={{ base: 'sm', md: 'xl', lg: '2xl' }}
						fontWeight="semibold"
						whiteSpace="nowrap"
					>
						{result.name}
					</Heading>
				</ChakraLink>
				<CloseButton
					aria-label="Close Solution Card"
					size="md"
					ml={6}
					onClick={onClickEvent}
				/>
			</Flex>
			<Flex
				borderBottom="thin dashed"
				borderColor="grey.100"
				pb={6}
				mb={5}
				gap="19px"
			>
				<Carousel>
					<CarouselBackArrow
						onClick={() =>
							GAAction({
								category: 'site_interaction',
								action: 'carousel_navigation',
							})
						}
					/>
					<CarouselForwardArrow
						onClick={() =>
							GAAction({
								category: 'site_interaction',
								action: 'carousel_navigation',
							})
						}
					/>
					{carouselSlides}
				</Carousel>
				<Flex flexDirection="column">
					<Flex alignItems="center">
						<PinIcn
							outer={coworkingSolutionSerpCardCss.addressInfo.pin.outer}
							inner={coworkingSolutionSerpCardCss.addressInfo.pin.inner}
							css={coworkingSolutionSerpCardCss.addressInfo.pin.pinCss}
						/>
						<Text color="grey.500">
							{displayAddress(result.address).replace(
								',',
								`, ${result.breadcrumbs.area.name}, `
							)}
						</Text>
					</Flex>
					<Flex gap="0.5rem" flexDirection={{ base: 'column', md: 'row' }}>
						{result.highlights.map((highlight, index) => (
							<Tag
								key={`serpcard-${result.id}-${index}`}
								color="light-blue"
								size="font12"
							>
								{t(highlight)}
							</Tag>
						))}
					</Flex>
				</Flex>
			</Flex>
			{solutionCount > 0 && (
				<Text color="grey.500">
					<b>{solutionCount}</b> {solutionCount > 1 ? t('lediga') : t('ledigt')}{' '}
					{t('kontor just nu')}
				</Text>
			)}
			<SummaryDisplay result={result} />
		</Box>
	)
})

CoworkingSolutionMapCard.displayName = 'CoworkingSolutionMapCard'
